import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "@/index.css";
import "@/styles/professional.css";
import App from "@/App";
import { initNativeShell, isNative, registerPush, deviceSignature } from "@/lib/native";
import { unlockWithBiometricIfNeeded } from "@/lib/biometricAuth";
import { verifyFirebaseConnection } from "@/lib/firebase";
import { api } from "@/lib/api";
import { initOfflineSync } from "@/lib/offlineQueue";
import { applyTextSize } from "@/lib/textSize";

// Apply saved text-size preference before first paint.
applyTextSize();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

async function bootstrap() {
  // Native shell — status bar, splash, back button, deep links.
  await initNativeShell();

  // Offline-first PWA — register the caching service worker at startup so the
  // app shell + last-known API data work without network, and start the
  // offline action queue auto-sync.
  const localPreview = process.env.NODE_ENV === "development" && ["localhost", "127.0.0.1"].includes(window.location.hostname);
  if ("serviceWorker" in navigator && localPreview) {
    // A cached development bundle can show yesterday's layout after a redesign.
    // Retire only our local worker; production offline support stays enabled.
    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(registrations.filter((r) => (r.active?.scriptURL || r.waiting?.scriptURL || "").endsWith("/firebase-messaging-sw.js")).map((r) => r.unregister()));
  } else if ("serviceWorker" in navigator) {
    navigator.serviceWorker.register("/firebase-messaging-sw.js", { scope: "/" })
      .then(() => console.info("[sw] offline cache + FCM service worker registered"))
      .catch((e) => console.warn("[sw] registration failed:", e));
  }
  initOfflineSync();

  // Firebase — verify SDK config so misconfiguration shows up early in the
  // console rather than as a surprise 401 later. Fire-and-forget; the app
  // continues to work even if Firebase is misconfigured (existing JWT auth
  // is entirely separate).
  verifyFirebaseConnection()
    .then((h) => {
      if (h.ok) console.info(`[firebase] ✓ Connected to project "${h.projectId}"`, h);
      else console.warn("[firebase] ✗ Not ready:", h.error);
    })
    .catch((e) => console.warn("[firebase] verify threw:", e));

  // Biometric unlock — if enabled, require Face ID/Touch ID before restoring
  // the previous session. If the user cancels, we drop the token and let
  // the app render as logged-out.
  if (isNative()) {
    const ok = await unlockWithBiometricIfNeeded();
    if (!ok) {
      try { localStorage.removeItem("icard_token"); } catch (_) {}
    }
  }

  const root = ReactDOM.createRoot(document.getElementById("root"));
  root.render(
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <App />
      </QueryClientProvider>
    </React.StrictMode>
  );

  // Push notifications — register + upsert the device token on the backend
  // as soon as a session is available. Runs post-render so we don't block
  // first paint.
  if (isNative()) {
    registerPush(
      async ({ token, platform }) => {
        try {
          const sig = await deviceSignature();
          await api.post("/notifications/register-device", {
            token,
            platform,
            ...sig,
          });
        } catch (_) { /* backend endpoint is best-effort */ }
      },
      (n) => {
        // Foreground push — surface via the existing toast infra when possible.
        try {
          window.dispatchEvent(new CustomEvent("icaard:push", { detail: n }));
        } catch (_) {}
      }
    );
  }
}

bootstrap();
